import { asKeys, labelForKey, pillarLabel, normalizeLevel, humanizeStatus, LEVEL_BADGE } from "./impactConstants";
import { STARTING_ATTITUDES } from "./stakeholderConstants";
import { choiceOf, valueOf } from "./segments";

// The fixed columns, after the segment ones. `messageId` is what the header
// cell says; several are the table's own column keys, so the sheet and the
// screen stay in step.
export const FIXED_COLUMNS = [
    { key: "what_is_changing", messageId: "impact.col_what_is_changing", fallback: "What is changing" },
    { key: "as_is", messageId: "impact.field_as_is", fallback: "As-is" },
    { key: "to_be", messageId: "impact.col_future_state", fallback: "Future state" },
    { key: "level", messageId: "impact.col_level", fallback: "Level" },
    { key: "starting_attitude", messageId: "stakeholder.field_attitude", fallback: "Starting attitude" },
    { key: "change_type", messageId: "impact.col_dimensions", fallback: "Dimensions" },
    { key: "change_actions", messageId: "impact.col_actions", fallback: "Actions" },
    { key: "success_measure", messageId: "impact.field_success_measure", fallback: "Success measure" },
    { key: "verification_status", messageId: "impact.col_status", fallback: "Status" },
];

// One column PER SEGMENT, first, in the project's order. On screen the
// segments share one stacked cell; here each is its own column, because the
// sheet carries an autofilter and a column is what you filter by — every cut
// the screen's segment bar can make, the sheet can make too.
export const columnsFor = (segments = []) => [
    ...segments.map((segment) => ({ key: `segment:${segment.id}`, segment, header: segment.title })),
    ...FIXED_COLUMNS,
];

const translate = (intl, id, fallback) =>
    intl.messages?.[id] ? intl.formatMessage({ id }) : fallback;

// A level/status cell reads as the word on screen, not the raw enum.
const levelCell = (intl, value) => {
    const key = normalizeLevel(value);
    if (!key || !LEVEL_BADGE[key]) return "";
    return translate(intl, `impact.level_${key}`, LEVEL_BADGE[key].short);
};

const attitudeCell = (intl, value) => {
    if (!value) return "";
    const key = String(value).toLowerCase();
    const known = STARTING_ATTITUDES.find((o) => o.value === key);
    return translate(intl, `stakeholder.attitude_${key}`, known ? known.label : humanizeStatus(value));
};

const statusCell = (intl, value) => {
    if (!value) return "";
    const key = String(value).toLowerCase();
    return translate(intl, `impact.status_${key}`, humanizeStatus(value));
};

// Taxonomy keys become their labels, joined — a spreadsheet has no room for chips
// and `org_structure` means nothing to a reader.
const taxonomyCell = (value, options) =>
    asKeys(value)
        .map((k) => labelForKey(options, k))
        .join(", ");

// Same, for the pillars: the canonical six follow the sheet's locale, a project's
// own pillars are its own words and go out verbatim.
const pillarCell = (intl, value, pillars) =>
    asKeys(value)
        .map((k) => pillarLabel(intl, pillars, k))
        .join(", ");

const cellFor = (impact, column, { intl, taxonomy }) => {
    // A segment cell is the choice's label, or empty when not set — an empty
    // cell is what an autofilter offers as "(Blanks)", which is the right word.
    if (column.segment) return choiceOf(column.segment, valueOf(impact, column.segment.id))?.label || "";
    switch (column.key) {
        case "level":
            return levelCell(intl, impact.level);
        case "starting_attitude":
            return attitudeCell(intl, impact.starting_attitude);
        case "verification_status":
            return statusCell(intl, impact.verification_status);
        case "change_type":
            return pillarCell(intl, impact.change_type, taxonomy.pillars);
        case "change_actions":
            return taxonomyCell(impact.change_actions, taxonomy.edges);
        default:
            return impact[column.key] || "";
    }
};

// The sheet as an array-of-arrays: header row then one row per impact. Split out
// from the file write so the shape can be asserted without touching the DOM.
export const buildSheetRows = ({ impacts, taxonomy, segments = [], intl }) => {
    const columns = columnsFor(segments);
    const header = columns.map((c) => c.header ?? translate(intl, c.messageId, c.fallback));
    const rows = impacts.map((impact) => columns.map((c) => cellFor(impact, c, { intl, taxonomy })));
    return [header, ...rows];
};

// ── Sheet names ─────────────────────────────────────────────────────────────
// Excel is strict about these and fails the whole write if one is wrong, so a
// name has to be made safe rather than trusted: at most 31 characters, none of : \ / ? * [ ], and unique within the
// workbook (case-insensitively, which is how Excel compares them).
const SHEET_NAME_MAX = 31;
const ILLEGAL_SHEET_CHARS = /[:\\/?*[\]]/g;

export const sanitizeSheetName = (name, taken = new Set()) => {
    let base = String(name || "")
        .replace(ILLEGAL_SHEET_CHARS, " ")
        .replace(/\s+/g, " ")
        .trim()
        // Excel also rejects a name that begins or ends with an apostrophe.
        .replace(/^'+|'+$/g, "")
        .slice(0, SHEET_NAME_MAX)
        .trim();
    if (!base) base = "Sheet";

    let candidate = base;
    let n = 2;
    while (taken.has(candidate.toLowerCase())) {
        const suffix = ` (${n})`;
        // Trim the BASE, not the suffix — the suffix is what makes it unique.
        candidate = `${base.slice(0, SHEET_NAME_MAX - suffix.length).trim()}${suffix}`;
        n += 1;
    }
    taken.add(candidate.toLowerCase());
    return candidate;
};

// ── Workbook shape ──────────────────────────────────────────────────────────
// One sheet, "All impacts": the whole register, whatever the screen is
// filtered to. It used to add a sheet per change leader, resolved through the
// group each leader owned; groups are gone, and with an autofilter on a column
// per segment, a sheet per slice is a filter click away rather than a tab.
//
// Returned as plain data so the shape can be asserted without xlsx.
export const buildWorkbookSheets = ({ impacts, taxonomy, segments = [], intl }) => {
    const name = sanitizeSheetName(translate(intl, "impact.rail_all_impacts", "All impacts"));
    return [
        {
            name,
            columns: columnsFor(segments),
            rows: buildSheetRows({ impacts, taxonomy, segments, intl }),
        },
    ];
};

// `impacts_payroll-migration_2026-08-20.xlsx`. The date is passed in rather than
// read from the clock so the name is deterministic under test.
export const buildFileName = (projectName, today) => {
    const slug = String(projectName || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // strip the accents NFD just split off
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
    const stamp = today.toISOString().slice(0, 10);
    return `impacts_${slug || "export"}_${stamp}.xlsx`;
};

// Rough column widths so the sheet is readable on open instead of every prose
// column collapsed to its header. Segment columns take the default.
const WIDTHS = {
    what_is_changing: 46,
    as_is: 34,
    to_be: 34,
    level: 10,
    starting_attitude: 16,
    change_type: 30,
    change_actions: 26,
    success_measure: 34,
    verification_status: 12,
};

// Build and hand the workbook to the browser. Client-side from data the
// workspace has already fetched — no export endpoint involved.
//
// `xlsx` is imported dynamically: it is ~94 kB gzipped and this is an occasional
// button, so it stays out of the main bundle and loads on first download.
export default async function exportImpactsToExcel({
    impacts,
    taxonomy,
    intl,
    projectName,
    segments = [],
    today = new Date(),
}) {
    const XLSX = await import("xlsx");
    const book = XLSX.utils.book_new();

    for (const sheetSpec of buildWorkbookSheets({ impacts, taxonomy, segments, intl })) {
        const sheet = XLSX.utils.aoa_to_sheet(sheetSpec.rows);
        sheet["!cols"] = sheetSpec.columns.map((c) => ({ wch: WIDTHS[c.key] || 18 }));
        sheet["!autofilter"] = {
            ref: XLSX.utils.encode_range({
                s: { c: 0, r: 0 },
                e: {
                    c: Math.max(sheetSpec.rows[0].length - 1, 0),
                    r: Math.max(sheetSpec.rows.length - 1, 1),
                },
            }),
        };
        XLSX.utils.book_append_sheet(book, sheet, sheetSpec.name);
    }

    const fileName = buildFileName(projectName, today);
    XLSX.writeFile(book, fileName);
    return fileName;
}
